import { Stack } from '@mui/system';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import RejectIcon from '@/assets/svg/ui-elements/close-square-filled-red.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Button, Modal } from '@/shared/design-system';

import {
  ActionInfoCard,
  ActionModalActionsSingle,
  ActionModalBody,
  ActionModalSeparator,
} from './action-modal-shared';
import {
  ActionReasonFields,
  ActionReasonValues,
  areActionReasonValuesValid,
  emptyActionReasonValues,
} from './action-reason-fields';

export type RejectActionModalProps = {
  type: 'fund' | 'entity';
  onConfirm: (reasons: ActionReasonValues) => void | Promise<void>;
  isLoading?: boolean;
  onContact: string;
};

const FUND_CONTENT = {
  bullets: ['Suspend the Fund', 'Hide public Fund information', 'Block new investments'],
  note: 'For non-critical issues, we recommend asking the Fund to make corrections and resubmit instead.',
};

const ENTITY_CONTENT = {
  bullets: [
    'Suspend the Entity',
    'Hide public Fund information for all Funds linked to the Entity',
    'Block new investments for all Funds linked to the Entity',
  ],
  note: 'For non-critical issues, we recommend asking the Entity to make corrections and resubmit instead.',
};

export const RejectActionModal = () => {
  const { open, handleClose, props } = useGlobalModal('REJECT_ACTION_MODAL');
  const [isPending, setIsPending] = useState(false);
  const [reasons, setReasons] = useState<ActionReasonValues>(emptyActionReasonValues);
  const navigate = useNavigate();

  const isFund = props?.type === 'fund';
  const subject = isFund ? 'Fund' : 'Entity';
  const content = isFund ? FUND_CONTENT : ENTITY_CONTENT;

  useEffect(() => {
    if (!open) setReasons(emptyActionReasonValues);
  }, [open]);

  const handleContactClick = () => {
    if (!props?.onContact) return;
    navigate(props.onContact);
    handleClose();
  };

  const handleConfirm = async () => {
    setIsPending(true);
    try {
      await props?.onConfirm({
        reason: reasons.reason.trim(),
        internalReference: reasons.internalReference.trim(),
      });
    } finally {
      setIsPending(false);
    }
  };

  const isLoading = isPending || props?.isLoading;
  const canConfirm = !isLoading && areActionReasonValuesValid(reasons);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (!o ? handleClose() : undefined)}
      title={`Reject ${subject}`}
      showClose
    >
      <ActionModalBody>
        <ActionModalSeparator />
        <ActionInfoCard
          icon={<RejectIcon width={24} height={24} />}
          title={`Are you sure you want to Reject this ${subject}?`}
          description={`Rejecting an active ${subject} will:`}
          bullets={content.bullets}
          note={content.note}
        />
        <ActionReasonFields
          sharedWithLabel={`Shared with the ${subject}`}
          values={reasons}
          onChange={setReasons}
          disabled={isLoading}
        />
        <ActionModalSeparator />
        <Stack flexDirection="column" gap="8px">
          <ActionModalActionsSingle>
            <Button
              color="destructive"
              variant="solid"
              size="lg"
              disabled={!canConfirm}
              onClick={handleConfirm}
              fullWidth
            >
              Reject
            </Button>
            <Button
              color="tertiary"
              variant="outline"
              size="lg"
              onClick={handleContactClick}
              fullWidth
            >
              Contact {subject}
            </Button>
          </ActionModalActionsSingle>
          <Button color="tertiary" variant="outline" size="lg" onClick={handleClose} fullWidth>
            Go Back
          </Button>
        </Stack>
      </ActionModalBody>
    </Modal>
  );
};
